import { Fragment, useState } from 'react'
import {
  IconChevronDown,
  IconChevronUp,
  IconChevronRight,
} from '@tabler/icons-react'

import { Loading, TrendLabel } from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn, formatNumber } from '@/utils'
import { Link } from 'react-router-dom'

interface TreeNode {
  id: string | number
  children?: TreeNode[]
  [key: string]: any
}

interface TreeTableProps {
  data: TreeNode[]
  columns: {
    header: string
    accessorKey: string
    type?: string
    isTrend?: boolean
  }[]
  isNormal?: boolean
  isCard?: boolean
  cardTitle?: string
  cardDescription?: string
  height?: number
  align?: 'left' | 'center' | 'right'
  hrefLinkOnName?: string
  loading?: boolean
  fitContent?: boolean
}

export function TreeTable({
  data,
  columns,
  isNormal = true,
  isCard = true,
  cardTitle,
  cardDescription,
  height = 55,
  align,
  hrefLinkOnName,
  loading,
  fitContent = false,
}: TreeTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set()
  )

  const toggleRow = (id: string | number) => {
    const newExpandedRows = new Set(expandedRows)
    if (expandedRows.has(id)) {
      newExpandedRows.delete(id)
    } else {
      newExpandedRows.add(id)
    }
    setExpandedRows(newExpandedRows)
  }

  const renderRow = (node: TreeNode, level: number = 0): JSX.Element => {
    const hasChildren = node.children && node.children.length > 0
    const isExpanded = expandedRows.has(node.id)

    return (
      <>
        <TableRow key={node.id}>
          {columns.map((column, columnIndex) => (
            <TableCell
              key={column.accessorKey}
              className={
                columnIndex === 0 ? 'sticky left-0 z-10 bg-white border-r' : ''
              }
              style={
                columnIndex === 0 && fitContent
                  ? { maxWidth: '150px', minWidth: '100px' }
                  : undefined
              }
            >
              {columnIndex === 0 && (
                <div
                  className="flex items-center"
                  style={{ paddingLeft: `${level * 25}px` }}
                >
                  {hasChildren && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 p-0 mr-2"
                      onClick={() => toggleRow(node.id)}
                    >
                      {isExpanded ? (
                        <IconChevronDown className="h-6 w-6 text-black-300" />
                      ) : (
                        <IconChevronRight className="h-6 w-6 text-black-300" />
                      )}
                    </Button>
                  )}
                  {hrefLinkOnName ? (
                    <Link
                      to={`${hrefLinkOnName}/${node.id}`}
                      className="hover:text-blue-500"
                    >
                      {node[column.accessorKey]}
                    </Link>
                  ) : (
                    node[column.accessorKey]
                  )}
                </div>
              )}
              {columnIndex !== 0 && (
                <div className="flex items-center gap-2">
                  {getDisplayData(
                    node,
                    column.accessorKey,
                    column.type,
                    column.isTrend
                  )}
                  {getPrevValue(node, column.accessorKey) && (
                    <TrendLabel
                      oldValue={getPrevValue(node, column.accessorKey)}
                      newValue={node[column.accessorKey]}
                      isNormal={isNormal}
                      trendType={
                        column.type as
                          | 'money'
                          | 'percentage'
                          | 'number'
                          | 'hour'
                      }
                      className="text-md"
                    />
                  )}
                </div>
              )}
            </TableCell>
          ))}
        </TableRow>
        {hasChildren &&
          isExpanded &&
          node.children?.map((child) => (
            <Fragment key={child.id}>{renderRow(child, level + 1)}</Fragment>
          ))}
      </>
    )
  }

  return (
    <>
      {isCard ? (
        <Card className="pt-2">
          {(cardTitle || cardDescription) && (
            <CardHeader>
              <CardTitle>{cardTitle}</CardTitle>
              <CardDescription>{cardDescription}</CardDescription>
            </CardHeader>
          )}
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center h-full my-10">
                <Loading />
              </div>
            ) : (
              <div
                className={`rounded-xl border overflow-x-auto h-[${height}vh] max-h-[${height}vh] overflow-y-auto`}
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map((column, index) => (
                        <TableHead
                          key={column.accessorKey}
                          className={`${
                            index === 0
                              ? 'sticky left-0 z-10 bg-background border-r'
                              : ''
                          } ${align === 'right' && index !== 0 ? 'text-right' : ''}`}
                          style={
                            index === 0 && fitContent
                              ? { maxWidth: '150px', minWidth: '100px' }
                              : undefined
                          }
                        >
                          {column.header}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.map((node) => (
                      <Fragment key={node.id}>{renderRow(node)}</Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex justify-center items-center h-full my-10">
          <Loading />
        </div>
      ) : (
        <div
          className={`rounded-xl border overflow-x-auto h-[${height}vh] max-h-[${height}vh] overflow-y-auto`}
        >
          <Table className="bg-white">
            <TableHeader>
              <TableRow>
                {columns.map((column, index) => (
                  <TableHead
                    key={column.accessorKey}
                    className={`${
                      index === 0 ? 'sticky left-0 z-10 bg-white border-r' : ''
                    } ${align === 'right' && index !== 0 ? 'text-right' : ''}`}
                    style={
                      index === 0 && fitContent
                        ? { maxWidth: '150px', minWidth: '100px' }
                        : undefined
                    }
                  >
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((node) => (
                <Fragment key={node.id}>{renderRow(node)}</Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  )
}

function getPrevValue(node: TreeNode, accessorKey: string) {
  const prevKey = `prev${accessorKey.charAt(0).toUpperCase()}${accessorKey.slice(1)}`

  return node[prevKey]
}

function getDisplayData(
  node: TreeNode,
  accessorKey: string,
  columnType?: string,
  isTrend?: boolean
) {
  const displayData = node[accessorKey]

  if (displayData === null || displayData === undefined) {
    return '-'
  }

  const type = columnType as 'money' | 'percentage' | 'number' | 'hour'

  if (isTrend) {
    let textColor = ''
    let trendDirection = 'neutral'
    if (node[accessorKey] > 0) {
      trendDirection = 'up'
    } else if (node[accessorKey] < 0) {
      trendDirection = 'down'
    }

    if (trendDirection === 'up') {
      textColor = 'text-green-500'
    } else if (trendDirection === 'down') {
      textColor = 'text-red-500'
    } else if (trendDirection === 'neutral') {
      textColor = 'text-yellow-500'
    }

    return (
      <span className={cn('text-xs font-medium flex items-center', textColor)}>
        {trendDirection === 'down' && <IconChevronDown className="h-4 w-4" />}
        {trendDirection === 'up' && <IconChevronUp className="h-4 w-4" />}
        {trendDirection === 'neutral' && node[accessorKey] === 0
          ? '0'
          : formatNumber(node[accessorKey], type)}
      </span>
    )
  }

  return formatNumber(displayData, type)
}
